import { get } from "../util/http";

/**
 * 公开端资讯接口
 *
 * 依据《资讯模块接口文档》第 1 节：
 * - 前缀：/api/news
 * - 公开端无鉴权，只返回已发布文章
 * - 统一响应信封：{ success, error, info, result, exception }
 * - 时间字段均为 epoch 毫秒；文章 id 为 24 位十六进制字符串
 * - 列表每页固定 9 条；category 为空 / all / 中文「全部」表示全部
 */

/** 公开端接口前缀；两种部署形态（material-web / 独立 news-service）一致 */
export const NEWS_API_BASE = "/api/news";

/** 公开端列表每页固定条数 */
export const NEWS_PAGE_SIZE = 9;

/** 公开端文章不存在（详情 / 推荐）的业务错误码 */
export const NEWS_INFO_ARTICLE_NOT_FOUND = "130001";

/**
 * 统一响应信封
 * @template T
 * @typedef {Object} NewsApiResponse
 * @property {boolean} success 是否成功
 * @property {string|null} error 错误描述，成功时为 null
 * @property {string|null} info 业务错误码（如 130001），成功时为 null
 * @property {T|null} result 业务数据，失败时为 null
 * @property {Object|null} exception 异常兜底信息，正常业务失败时为 null
 */

/**
 * 文章状态
 * @typedef {"draft"|"published"} NewsArticleStatus
 */

/**
 * 文章对象（公开端 / 管理端共用）
 * 列表、推荐、上下篇场景 content 恒为 null；详情才带 Markdown 正文。
 * @typedef {Object} NewsArticle
 * @property {string} id
 * @property {string} title
 * @property {string|null} summary
 * @property {string} author
 * @property {string} category 分类 code，可能是字典 code 或自动生成的 C+10 位大写十六进制
 * @property {string} categoryLabel
 * @property {string|null} cover 封面 OSS 地址，无封面为 null
 * @property {NewsArticleStatus} status
 * @property {number} tmCreate 创建时间（epoch 毫秒）
 * @property {number|null} tmPublished 发布时间（epoch 毫秒）；草稿为 null
 * @property {boolean} pinned
 * @property {number|null} pinOrder 置顶序号（1..N）；未置顶为 null
 * @property {string|null} sourceFilename 上传的 md 原始文件名
 * @property {string|null} content Markdown 正文；列表 / 推荐 / 上下篇为 null
 */

/**
 * 上一篇 / 下一篇 / 推荐位的基础文章对象
 * @typedef {Object} NewsArticleBrief
 * @property {string} id
 * @property {string} title
 * @property {null} content 恒为 null
 */

/**
 * 分页结构（PageDataDto）
 * @typedef {Object} NewsPageData
 * @property {number} total 符合条件的总条数
 * @property {number} page 当前页码，从 1 起
 * @property {number} pageSize 每页条数
 * @property {boolean} hasNext 是否存在下一页
 * @property {NewsArticle[]} data 当前页数据
 */

/**
 * 公开端分类 chip
 * @typedef {Object} NewsCategoryOption
 * @property {string} value 分类 code，用于列表筛选入参
 * @property {string} label 分类展示名称
 */

/**
 * 文章详情 result
 * @typedef {Object} NewsDetailResult
 * @property {NewsArticle} article 文章详情（含 content）
 * @property {NewsArticleBrief|null} prev 上一篇（更早发布，跨分类）
 * @property {NewsArticleBrief|null} next 下一篇（更新发布，跨分类）
 * @property {boolean} prevEnd 是否已到最早一篇
 * @property {boolean} nextEnd 是否已到最新一篇
 */

/**
 * 文章推荐 result
 * @typedef {Object} NewsRecommendResult
 * @property {NewsArticleBrief[]} similar 同分类、排除自身，最多 4 篇
 * @property {NewsArticleBrief[]} latest 排除自身、发布时间倒序，最多 3 篇
 */

/**
 * 列表查询入参
 * @typedef {Object} NewsArticleListParams
 * @property {string} [category] 分类 code；空 / all / 中文「全部」表示全部
 * @property {number} [page] 页码，从 1 起，默认 1
 */

/**
 * 拼查询串：跳过 undefined / null / 空串，保留 all 等业务值。
 * @param {string} path
 * @param {Record<string, string|number|null|undefined>} params
 */
function withQuery(path, params) {
  const search = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? path + "?" + query : path;
}

/**
 * 1.1 资讯列表分页查询
 *
 * GET /api/news/articles?category=PRODUCT_NEWS&page=1
 * - 每页固定 9 条；页码超过总页数时后端回落到最后一页；
 * - 全部 Tab：置顶优先（pinOrder 升序），其余按发布时间倒序、id 倒序；
 * - 分类 Tab：只按发布时间倒序、id 倒序，不参与置顶排序。
 *
 * 返回统一信封，result 为 NewsPageData。
 *
 * @param {NewsArticleListParams} [params]
 * @returns {Promise<NewsApiResponse<NewsPageData>>}
 */
export function queryNewsArticles(params = {}) {
  const { category, page } = params || {};
  return get({
    url: withQuery(NEWS_API_BASE + "/articles", { category, page }),
  });
}

/**
 * 1.2 资讯分类（公开端 chips）
 *
 * GET /api/news/categories
 * - 只返回有已发布文章的分类，按分类字典 sortOrder 升序；
 * - 不返回文章数量；字典中已不存在的历史编码会兜底返回（按编码排序，label 用编码本身）。
 *
 * 返回统一信封，result 为 NewsCategoryOption[]。
 *
 * @returns {Promise<NewsApiResponse<NewsCategoryOption[]>>}
 */
export function queryNewsCategories() {
  return get({ url: NEWS_API_BASE + "/categories" });
}

/**
 * 1.3 文章详情
 *
 * GET /api/news/detail?id=xxx
 * - article 含 content（Markdown 字符串，图片地址已转存 OSS）；
 * - prev / next 只返回基础字段，content 恒为 null；
 * - 文章不存在或为草稿：success=false，info=130001。
 *
 * 返回统一信封，result 为 NewsDetailResult。
 *
 * @param {string} id 文章 id（24 位十六进制）
 * @returns {Promise<NewsApiResponse<NewsDetailResult>>}
 */
export function queryNewsDetail(id) {
  return get({
    url: withQuery(NEWS_API_BASE + "/detail", { id }),
  });
}

/**
 * 1.4 文章推荐（相似 + 最新）
 *
 * GET /api/news/recommend?id=xxx
 * - similar：同分类、排除自身、发布时间倒序，最多 4 篇；
 * - latest：排除自身、发布时间倒序，最多 3 篇；
 * - 文章不存在（或草稿）：success=false，info=130001。
 *
 * 返回统一信封，result 为 NewsRecommendResult。
 *
 * @param {string} id 文章 id（24 位十六进制）
 * @returns {Promise<NewsApiResponse<NewsRecommendResult>>}
 */
export function queryNewsRecommend(id) {
  return get({
    url: withQuery(NEWS_API_BASE + "/recommend", { id }),
  });
}
